import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, RefreshCw, Inbox, Check, X, Star } from "lucide-react";
import Logo from "@/components/Logo";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry && !original.url.includes("/auth/")) {
      original._retry = true;
      try {
        await api.post("/auth/refresh");
        return api(original);
      } catch {
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

const STATUS_LABELS = { nuova: "Nuova", contattata: "Contattata", confermata: "Confermata" };
const STATUS_STYLES = {
  nuova: "bg-terracotta/15 text-terracotta",
  contattata: "bg-sand/60 text-moss",
  confermata: "bg-sage/25 text-pine",
};
const REVIEW_LABELS = { in_attesa: "In attesa", approvata: "Approvata", rifiutata: "Rifiutata" };
const REVIEW_STYLES = {
  in_attesa: "bg-terracotta/15 text-terracotta",
  approvata: "bg-sage/25 text-pine",
  rifiutata: "bg-sand/60 text-moss",
};

function StarsRow({ value }) {
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={13} strokeWidth={1.5} className={n <= value ? "fill-terracotta text-terracotta" : "text-sand"} />
      ))}
    </span>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("prenotazioni");
  const [requests, setRequests] = useState(null);
  const [reviews, setReviews] = useState(null);
  const [showAddReview, setShowAddReview] = useState(false);
  const [newReview, setNewReview] = useState({ nome: "", eta: "", stelle: 5, motivo: "", fonte: "", testo: "" });

  const addReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews/admin", {
        ...newReview,
        eta: parseInt(newReview.eta, 10),
        stelle: parseInt(newReview.stelle, 10),
      });
      toast.success("Recensione aggiunta e pubblicata");
      setShowAddReview(false);
      setNewReview({ nome: "", eta: "", stelle: 5, motivo: "", fonte: "", testo: "" });
      load();
    } catch {
      toast.error("Inserimento non riuscito");
    }
  };
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [reqRes, revRes] = await Promise.all([api.get("/requests"), api.get("/reviews/all")]);
      setRequests(reqRes.data);
      setReviews(revRes.data);
    } catch {
      navigate("/admin/login");
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/requests/${id}`, { status });
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Stato aggiornato");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const removeRequest = async (id) => {
    try {
      await api.delete(`/requests/${id}`);
      setRequests((prev) => prev.filter((r) => r.id !== id));
      toast.success("Richiesta eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const setReviewStatus = async (id, status) => {
    try {
      await api.patch(`/reviews/${id}`, { status });
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(status === "approvata" ? "Recensione pubblicata" : "Recensione rifiutata");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const removeReview = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Recensione eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      navigate("/admin/login");
    }
  };

  const pendingReviews = reviews ? reviews.filter((r) => r.status === "in_attesa").length : 0;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-bone text-pine font-sans">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-bone/85 border-b border-pine/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              data-testid="refresh-button"
              onClick={load}
              className="flex items-center gap-2 text-sm font-medium text-moss hover:text-pine border border-pine/20 rounded-full px-4 py-2 transition-colors duration-300"
            >
              <RefreshCw size={15} strokeWidth={1.5} /> Aggiorna
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-sm font-semibold text-bone bg-pine rounded-full px-4 py-2 hover:bg-terracotta transition-colors duration-300"
            >
              <LogOut size={15} strokeWidth={1.5} /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-2 border-b border-pine/10">
          <button
            data-testid="tab-prenotazioni"
            onClick={() => setTab("prenotazioni")}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors duration-300 ${
              tab === "prenotazioni" ? "border-terracotta text-pine" : "border-transparent text-moss hover:text-pine"
            }`}
          >
            Prenotazioni
          </button>
          <button
            data-testid="tab-recensioni"
            onClick={() => setTab("recensioni")}
            className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors duration-300 ${
              tab === "recensioni" ? "border-terracotta text-pine" : "border-transparent text-moss hover:text-pine"
            }`}
          >
            Recensioni
            {pendingReviews > 0 && (
              <span data-testid="pending-reviews-badge" className="ml-2 bg-terracotta text-bone text-xs rounded-full px-2 py-0.5">
                {pendingReviews}
              </span>
            )}
          </button>
        </div>

        {tab === "prenotazioni" && (
          <>
            {!requests ? (
              <p className="mt-12 text-moss" data-testid="requests-loading">Caricamento...</p>
            ) : requests.length === 0 ? (
              <div data-testid="requests-empty" className="mt-12 bg-paper border border-pine/10 rounded-md p-16 flex flex-col items-center text-center">
                <Inbox size={36} strokeWidth={1.5} className="text-sand" />
                <p className="mt-4 font-serif text-xl">Nessuna richiesta per ora</p>
                <p className="mt-2 text-sm text-moss">Le richieste inviate dal modulo contatti appariranno qui.</p>
              </div>
            ) : (
              <div className="mt-8 overflow-x-auto bg-paper border border-pine/10 rounded-md">
                <table className="w-full text-sm min-w-[760px]">
                  <thead>
                    <tr className="border-b border-pine/10 text-left">
                      {["Paziente", "Telefono", "Servizio", "Messaggio", "Data", "Stato", ""].map((h) => (
                        <th key={h} className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-moss">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} data-testid={`request-row-${r.id}`} className="border-b border-pine/5 hover:bg-cream/60 transition-colors duration-200 align-top">
                        <td className="px-5 py-4 font-semibold">{r.nome}</td>
                        <td className="px-5 py-4">
                          <a href={`tel:${r.telefono}`} className="text-terracotta hover:underline">{r.telefono}</a>
                        </td>
                        <td className="px-5 py-4">{r.servizio}</td>
                        <td className="px-5 py-4 text-moss max-w-[220px]">
                          <span className="line-clamp-3">{r.messaggio || "—"}</span>
                        </td>
                        <td className="px-5 py-4 text-moss whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-5 py-4">
                          <select
                            data-testid={`status-select-${r.id}`}
                            value={r.status}
                            onChange={(e) => setStatus(r.id, e.target.value)}
                            className={`rounded-full text-xs font-semibold px-3 py-1.5 border-0 outline-none cursor-pointer ${STATUS_STYLES[r.status]}`}
                          >
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            data-testid={`delete-button-${r.id}`}
                            onClick={() => removeRequest(r.id)}
                            className="text-moss hover:text-terracotta transition-colors duration-200"
                            aria-label="Elimina richiesta"
                          >
                            <Trash2 size={16} strokeWidth={1.5} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {tab === "recensioni" && (
          <>
            <div className="mt-6 flex justify-end">
              <button
                data-testid="add-review-button"
                onClick={() => setShowAddReview(!showAddReview)}
                className="text-sm font-semibold text-bone bg-pine rounded-full px-5 py-2.5 hover:bg-terracotta transition-colors duration-300"
              >
                + Aggiungi recensione
              </button>
            </div>
            {showAddReview && (
              <form
                data-testid="add-review-form"
                onSubmit={addReview}
                className="mt-4 bg-paper border border-pine/10 rounded-md p-6 grid sm:grid-cols-2 gap-4"
              >
                <input
                  data-testid="add-review-name"
                  required
                  placeholder="Nome e cognome"
                  value={newReview.nome}
                  onChange={(e) => setNewReview({ ...newReview, nome: e.target.value })}
                  className="bg-transparent border border-pine/20 rounded-md focus:border-terracotta outline-none py-2.5 px-4 text-sm"
                />
                <input
                  data-testid="add-review-age"
                  required
                  type="number"
                  min="1"
                  max="110"
                  placeholder="Età"
                  value={newReview.eta}
                  onChange={(e) => setNewReview({ ...newReview, eta: e.target.value })}
                  className="bg-transparent border border-pine/20 rounded-md focus:border-terracotta outline-none py-2.5 px-4 text-sm"
                />
                <select
                  data-testid="add-review-stars"
                  value={newReview.stelle}
                  onChange={(e) => setNewReview({ ...newReview, stelle: e.target.value })}
                  className="bg-transparent border border-pine/20 rounded-md focus:border-terracotta outline-none py-2.5 px-4 text-sm"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>{n} stelle</option>
                  ))}
                </select>
                <select
                  data-testid="add-review-source"
                  value={newReview.fonte}
                  onChange={(e) => setNewReview({ ...newReview, fonte: e.target.value })}
                  className="bg-transparent border border-pine/20 rounded-md focus:border-terracotta outline-none py-2.5 px-4 text-sm"
                >
                  <option value="">Fonte: nessuna</option>
                  <option value="Google">da Google</option>
                  <option value="MioDottore">da MioDottore</option>
                </select>
                <input
                  data-testid="add-review-reason"
                  placeholder="Motivo del percorso (facoltativo)"
                  value={newReview.motivo}
                  onChange={(e) => setNewReview({ ...newReview, motivo: e.target.value })}
                  className="sm:col-span-2 bg-transparent border border-pine/20 rounded-md focus:border-terracotta outline-none py-2.5 px-4 text-sm"
                />
                <textarea
                  data-testid="add-review-text"
                  required
                  rows={3}
                  placeholder="Testo della recensione"
                  value={newReview.testo}
                  onChange={(e) => setNewReview({ ...newReview, testo: e.target.value })}
                  className="sm:col-span-2 bg-transparent border border-pine/20 rounded-md focus:border-terracotta outline-none py-2.5 px-4 text-sm resize-none"
                />
                <button
                  data-testid="add-review-submit"
                  type="submit"
                  className="sm:col-span-2 rounded-full bg-terracotta text-bone font-semibold py-3 hover:bg-pine transition-colors duration-300"
                >
                  Aggiungi e pubblica
                </button>
              </form>
            )}
            {!reviews ? (
              <p className="mt-12 text-moss" data-testid="reviews-loading">Caricamento...</p>
            ) : reviews.length === 0 ? (
              <div data-testid="admin-reviews-empty" className="mt-12 bg-paper border border-pine/10 rounded-md p-16 flex flex-col items-center text-center">
                <Star size={36} strokeWidth={1.5} className="text-sand" />
                <p className="mt-4 font-serif text-xl">Nessuna recensione ricevuta</p>
                <p className="mt-2 text-sm text-moss">Le recensioni lasciate dai pazienti appariranno qui per l'approvazione.</p>
              </div>
            ) : (
              <div className="mt-8 overflow-x-auto bg-paper border border-pine/10 rounded-md">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b border-pine/10 text-left">
                      {["Nome", "Età", "Stelle", "Motivo", "Testo", "Data", "Stato", "Azioni"].map((h) => (
                        <th key={h} className="px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-moss">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reviews.map((r) => (
                      <tr key={r.id} data-testid={`review-row-${r.id}`} className="border-b border-pine/5 hover:bg-cream/60 transition-colors duration-200 align-top">
                        <td className="px-5 py-4 font-semibold">{r.nome}</td>
                        <td className="px-5 py-4">{r.eta}</td>
                        <td className="px-5 py-4"><StarsRow value={r.stelle} /></td>
                        <td className="px-5 py-4 text-moss max-w-[160px]">
                          <span className="line-clamp-2">{r.motivo || "—"}</span>
                        </td>
                        <td className="px-5 py-4 text-moss max-w-[260px]">
                          <span className="line-clamp-3">{r.testo}</span>
                        </td>
                        <td className="px-5 py-4 text-moss whitespace-nowrap">
                          {new Date(r.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`rounded-full text-xs font-semibold px-3 py-1.5 ${REVIEW_STYLES[r.status]}`}>
                            {REVIEW_LABELS[r.status]}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2">
                            {r.status !== "approvata" && (
                              <button
                                data-testid={`approve-review-${r.id}`}
                                onClick={() => setReviewStatus(r.id, "approvata")}
                                className="flex items-center gap-1 text-xs font-semibold text-pine bg-sage/25 rounded-full px-3 py-1.5 hover:bg-sage/50 transition-colors duration-200"
                              >
                                <Check size={13} strokeWidth={2} /> Approva
                              </button>
                            )}
                            {r.status !== "rifiutata" && (
                              <button
                                data-testid={`reject-review-${r.id}`}
                                onClick={() => setReviewStatus(r.id, "rifiutata")}
                                className="flex items-center gap-1 text-xs font-semibold text-terracotta bg-terracotta/15 rounded-full px-3 py-1.5 hover:bg-terracotta/30 transition-colors duration-200"
                              >
                                <X size={13} strokeWidth={2} /> Rifiuta
                              </button>
                            )}
                            <button
                              data-testid={`delete-review-${r.id}`}
                              onClick={() => removeReview(r.id)}
                              className="text-moss hover:text-terracotta transition-colors duration-200"
                              aria-label="Elimina recensione"
                            >
                              <Trash2 size={16} strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
